import {FC, useRef} from 'react';
import {AriaTableProps, useTable} from 'react-aria';
import {
    Cell,
    CellProps,
    Column,
    ColumnProps,
    Row,
    RowProps,
    TableBody,
    TableBodyProps,
    TableHeader,
    TableHeaderProps,
    TableStateProps,
    useTableState,
} from 'react-stately';

import TableStatic, {TableStaticProps} from '../TableStatic';
import {TableStaticCellProps} from '../TableStatic/TableStaticCell';
import {TableStaticHeadCellProps} from '../TableStatic/TableStaticHeadCell';
import TableCell from './TableCell';
import {TableCheckboxCell, TableSelectAllCell} from './TableCheckbox';
import TableColumnBody from './TableColumnBody';
import TableColumnHeader from './TableColumnHeader';
import TableHeadBody from './TableHeaderBody';
import TableHeaderRow from './TableHeaderRow';
import TableRow from './TableRow';

type FunctionalCellProps = {preventPointerEvents?: boolean} & CellProps &
    TableStaticCellProps;

type FunctionalRowProps<T> = {className?: string} & RowProps<T>;

type TableComponentProps = AriaTableProps &
    TableStateProps<object> &
    TableStaticProps;

type TableProps = {
    Body: <T>(props: TableBodyProps<T>) => JSX.Element;
    Cell: (props: FunctionalCellProps) => JSX.Element;
    Column: <T>(
        props: ColumnProps<T> & TableStaticHeadCellProps,
    ) => JSX.Element;
    Header: <T>(props: TableHeaderProps<T>) => JSX.Element;
    Row: <T>(props: FunctionalRowProps<T>) => JSX.Element;
} & FC<TableComponentProps>;

const Table: TableProps = (props) => {
    const {className, classNameTable, hasStripes, selectionMode} = props;

    const showSelectionCheckboxes =
        selectionMode === 'multiple' || selectionMode === 'single';

    const state = useTableState({
        ...props,
        showSelectionCheckboxes,
    });

    const ref = useRef(null);
    const {collection} = state;
    const {gridProps} = useTable(props, state, ref);

    return (
        <TableStatic
            className={className}
            classNameTable={classNameTable}
            hasStripes={hasStripes}
            ref={ref}
            {...gridProps}
        >
            <TableHeadBody>
                {collection.headerRows.map((headerRow) => (
                    <TableHeaderRow
                        item={headerRow}
                        key={headerRow.key}
                        state={state}
                    >
                        {[...headerRow.childNodes].map((column) =>
                            column.props.isSelectionCell ? (
                                <TableSelectAllCell
                                    column={column}
                                    key={column.key}
                                    state={state}
                                />
                            ) : (
                                <TableColumnHeader
                                    column={column}
                                    key={column.key}
                                    state={state}
                                />
                            ),
                        )}
                    </TableHeaderRow>
                ))}
            </TableHeadBody>
            <TableColumnBody>
                {[...collection.body.childNodes].map((row) => (
                    <TableRow
                        className={className}
                        item={row}
                        key={row.key}
                        state={state}
                    >
                        {[...row.childNodes].map((cell) =>
                            cell.props.isSelectionCell ? (
                                <TableCheckboxCell
                                    cell={cell}
                                    key={cell.key}
                                    state={state}
                                />
                            ) : (
                                <TableCell
                                    cell={cell}
                                    key={cell.key}
                                    state={state}
                                />
                            ),
                        )}
                    </TableRow>
                ))}
            </TableColumnBody>
        </TableStatic>
    );
};

Table.Body = TableBody as TableProps['Body'];
Table.Cell = Cell as TableProps['Cell'];
Table.Column = Column as TableProps['Column'];
Table.Header = TableHeader as TableProps['Header'];
Table.Row = Row as TableProps['Row'];

export default Table;
